import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Modal } from "antd";
import { StyledModalWrapper } from "./styles";
import type { DraggableData, DraggableEvent } from 'react-draggable';
import Draggable from 'react-draggable';

interface StyledModalProps {
  title: React.ReactNode;
  open: boolean;
  closeIcon?: React.ReactNode;
  footer?: React.ReactNode | null;
  centered?: boolean;
  onCancel?: () => void;
  children: React.ReactNode;
  width?: string | number;
  maxHeight?: string | number;
  minHeight?: string | number;
  draggable?: boolean;
}

const StyledModal: React.FC<StyledModalProps> = ({ title, open, closeIcon, footer, centered = false, onCancel = () => { }, children, width, maxHeight, minHeight, draggable = false }) => {
  const [disabled, setDisabled] = useState(true);
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  // Create a ref with non-null assertion to match the expected type
  const draggleRef = useRef<HTMLDivElement>(null!);

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const renderTitle = () => {
    if (!draggable) return title;
    
    return (
      <div
        style={{ width: '100%', cursor: 'move' }}
        onMouseOver={() => {
          if (disabled) {
            setDisabled(false);
          }
        }}
        onMouseOut={() => {
          setDisabled(true);
        }}
        onFocus={() => {}}
        onBlur={() => {}}
      >
        {title}
      </div>
    );
  };

  // Create a style object for the modal body
  const bodyStyle = {
    maxHeight: maxHeight ? maxHeight : "calc(100dvh - 270px)",
    ...(minHeight ? { minHeight: minHeight } : {}),
    overflowY: "auto" as const,
    padding: "0 20px",
  };

  const modalProps = {
    width,
    title: renderTitle(),
    open,
    closeIcon,
    footer,
    centered,
    onCancel,
    bodyStyle, // Use bodyStyle prop instead of styles.body
  };

  if (!draggable) {
    return <StyledModalWrapper {...modalProps}>{children}</StyledModalWrapper>;
  }

  return (
    <StyledModalWrapper
      {...modalProps}
      modalRender={(modal) => (
        <Draggable
          disabled={disabled}
          bounds={bounds}
          nodeRef={draggleRef}
          onStart={(event: DraggableEvent, uiData: DraggableData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
    >
      {children}
    </StyledModalWrapper>
  );
};

export default StyledModal;
